import axios from "axios";
import dayjs from "dayjs";
import { BASE_API_URI, BASE_API_V2_URI, BASE_REPORT_SCHEDULER_URI, BASE_STIMULSOFT_URL } from "../constant";
import { SESSION_REFRESH_TOKEN } from "./apiPath";
import { expiredSessionHelper } from "../helper";

// Convert PascalCase/camelCase field names to readable labels.
// "SamFileName" -> "SAM File Name", "footnoteSickDays" -> "Footnote Sick Days"
const prettifyFieldName = name => {
  if (!name || typeof name !== "string") return "";
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/^./, c => c.toUpperCase())
    .trim();
};

// Extract a human-readable error message from any API response shape we know about.
// Covers: new API { error: { message, details: [{ message, target }] } },
// legacy { data, message }, ASP.NET ModelState { errors: { Field: [msg] } }, plain strings.
const extractApiErrorMessage = data => {
  if (!data) return null;
  if (typeof data === "string") return data;

  if (typeof data.error === "string") return data.error;

  if (data.error && typeof data.error === "object") {
    const details = Array.isArray(data.error.details) ? data.error.details : [];
    if (details.length > 0) {
      return details
        .map(d => {
          const label = prettifyFieldName(d?.target);
          const msg = (d?.message || "").replace(/^The \S+ field/, "This field");
          return label ? `${label}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join("\n");
    }
    if (typeof data.error.message === "string") return data.error.message;
  }

  if (typeof data.message === "string") return data.message;

  if (data.errors) {
    if (Array.isArray(data.errors) && data.errors.length > 0) return data.errors.join("\n");
    if (typeof data.errors === "object") {
      const allMessages = Object.values(data.errors)
        .flatMap(fieldErrors => (Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors]))
        .filter(msg => typeof msg === "string" && msg.trim());
      if (allMessages.length > 0) return allMessages.join("\n");
    }
  }

  return null;
};

axios.interceptors.request.use(config => {
  const authToken = localStorage.getItem("accessToken");

  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => {
    // give the raw response for blob types to get all headers
    if (response.config.responseType === "blob") {
      return response;
    }

    if (response.status === 204) {
      return {};
    }

    if (
      (response.status !== 200 && response.status !== 201) ||
      (Object.hasOwn(response.data || {}, "success") && !response.data.success) ||
      (Object.hasOwn(response.data || {}, "statusCode") && response.data.statusCode !== 200 && response.data.statusCode !== 201)
    ) {
      if (response.data?.message) {
        throw new Error(response.data.message);
      } else throw new Error(response);
    }

    if (typeof response.data === "string") {
      return response.data;
    }

    if (Object.hasOwn(response.data, "value")) {
      return { data: response.data.value, totalRecords: response.data["@odata.count"] ?? 0, headers: response.headers };
    }

    if (Object.hasOwn(response.data, "data")) {
      return response.data;
    }

    return response.data;
  },
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken");
      window.location.href = "/";
      return;
    } else if (error.response?.status === 500) {
      throw new Error("Oops! Something went wrong on our end. Please try again later.");
    } else if (error.response?.status === 409) {
      const conflict = new Error(extractApiErrorMessage(error.response?.data) || "Oops! Something went wrong on our end. Please try again later.");
      conflict.status = 409;
      throw conflict;
    }
    const message =
    extractApiErrorMessage(error?.response?.data) ||
    error?.message ||
    (error?.code === "ECONNABORTED" ? "Request timed out." : null) ||
    (error?.response?.status ? `Request failed with status ${error.response.status}` : null) ||
    "Oops! Something went wrong on our end. Please try again later.";

    const err = new Error(message);
    err.status = error?.response?.status;
    throw err;
  }
);

export const apiRequest = async ({ method, url, req, params }) => {
  if (await refreshToken()) {
    try {
      let baseURL = BASE_API_URI;

      if (url.startsWith("[V2]")) {
        url = url.replace("[V2]", "");
        baseURL = BASE_API_V2_URI;
      } else if (url.startsWith("[SCHED]")) {
        url = url.replace("[SCHED]", "");
        if (!BASE_REPORT_SCHEDULER_URI) {
          throw new Error("ReportScheduler URL is not configured. Set REACT_APP_REPORT_SCHEDULER_API_URL.");
        }
        baseURL = BASE_REPORT_SCHEDULER_URI;
      } else if (url.startsWith("[ReportAPI]")) {
        url = url.replace("[ReportAPI]", "");
        baseURL = BASE_STIMULSOFT_URL;
      }

      return await axios.request({
        method,
        baseURL,
        url,
        ...(method === "get" ? (params || {}) : { data: req, ...(params || {}) })
      });
    } catch (error) {
      return { error: error?.message || error, statusCode: error?.status };
    }
  } else {
    expiredSessionHelper();
    return { error: "Session expired" };
  }
};
export const getApi = async (url, params) => await apiRequest({ method: "get", url, params });
export const postApi = async (url, req, params) => await apiRequest({ method: "post", url, req, params });
export const putApi = async (url, req, params) => await apiRequest({ method: "put", url, req, params });
export const deleteApi = async (url, req, params) => await apiRequest({ method: "delete", url, req, params });
export const patchApi = async (url, req, params) => await apiRequest({ method: "patch", url, req, params });

// Returns true if successfully refreshed or refresh is not necessary
// Returns false if the token is expired or refresh is unsuccessful
export const refreshToken = async () => {
  const currentToken = localStorage.getItem("accessToken");
  const expirationTime = localStorage.getItem("expiresIn");

  // If we don't have a token, don't attempt to refresh.
  if (!currentToken) return true;

  const expiration = dayjs(expirationTime);
  const now = dayjs();

  if (expiration.isAfter(now)) {
    // Check if we're within 10 minutes of expiring, and if so refresh the token.
    if (expiration.diff(now, "minute") <= 10) {
      try {
        // Directly call api to circumvent normal interceptors
        const result = await axios.request({
          method: "post",
          baseURL: BASE_API_V2_URI,
          url: SESSION_REFRESH_TOKEN,
          data: {
          token: currentToken
          }
        });

        if (result?.accessToken) {
          localStorage.setItem("accessToken", result.accessToken);
          localStorage.setItem("expiresIn", result.expiresIn);
        } else {
          return false;
        }
      } catch (err) {
        console.error(err);
        return false;
      }
    }
  } else {
    return false;
  }

  return true;
};